import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import Raven from 'raven-js';
import { getUpgradePointPromise, getUpgradeDataTrackingProperties, upgradePointViews, pointTypes, warnArchivedUpgradePointUse } from './utils';
import RemoveBrandingBanner from './banner/RemoveBrandingBanner';
import LoadingBanner from './banner/LoadingBanner';
import UsageLimitBanner from './banner/UsageLimitBanner';
import LimitPackBanner from './banner/LimitPackBanner';
import { USAGE_LIMIT_BANNER } from '../_core/common/constants/PurchaseMotionLocations';
import { getCanViewNewSeats } from '../_core/common/eligibility/getCanViewNewSeats';
import { getIsFreePortal } from '../_core/common/eligibility/getIsFreePortal'; // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'self... Remove this comment to see the full error message

import { getLocationAwareProductPurchaseMotionData } from 'self-service-api/api/getProductPurchaseMotionData';

const getCorrectedUpgradePoint = upgradePoint => {
  upgradePoint.upgradeProduct = upgradePoint.upgradeProducts[0];
  upgradePoint.apiName = upgradePoint.apiNames[0];
  return upgradePoint;
};

const UIUpgradeBanner = ({
  upgradeKey,
  className,
  limit,
  value,
  displayThreshold,
  style = {},
  'data-test-id': dataTestId
}) => {
  const [bannerData, setBannerData] = useState();
  const [isFreePortal, setIsFreePortal] = useState(null);
  const [hasError, setHasError] = useState(false);
  const upgradePointPromise = getUpgradePointPromise({
    upgradeKey,
    delayCall: false
  });
  const [purchaseMotionResponse, setPurchaseMotionResponse] = useState();
  const [canViewNewSeats, setCanViewNewSeats] = useState(false);
  const isValueBelowDisplayThreshold = useCallback(() => {
    return displayThreshold && value !== undefined && value >= 0 && value < displayThreshold;
  }, [displayThreshold, value]); // Fetch upgrade config and track view

  useEffect(() => {
    Promise.all([getCanViewNewSeats(), getIsFreePortal(), upgradePointPromise]).then(([newSeatsResponse, isfreePortalRes, upgradePoint]) => {
      setCanViewNewSeats(newSeatsResponse);
      if (!upgradePoint) return;
      const correctedUpgradePoint = getCorrectedUpgradePoint(upgradePoint);

      if (!upgradePointViews[upgradeKey]) {
        upgradePointViews[upgradeKey] = true;

        if (correctedUpgradePoint.archived) {
          warnArchivedUpgradePointUse(correctedUpgradePoint.upgradeKey);
        }

        let limitInteractionTrackingData = {};

        if ((correctedUpgradePoint.pointType === pointTypes.usageLimitBanner || correctedUpgradePoint.pointType === pointTypes.limitPackBanner) && limit && value !== undefined && value >= 0) {
          const hasReachedLimit = value >= limit;
          limitInteractionTrackingData = {
            limit,
            value,
            displayThreshold,
            hasReachedLimit
          };
        }

        if (!isValueBelowDisplayThreshold()) {
          track('interaction', Object.assign({
            action: 'viewed upgrade point'
          }, limitInteractionTrackingData, {}, getUpgradeDataTrackingProperties(correctedUpgradePoint, true, true)));
        }
      }

      setBannerData(correctedUpgradePoint);
      setIsFreePortal(isfreePortalRes);
      setHasError(false);
      return;
    }).catch(err => {
      console.error(err);
      setHasError(true);
    });
  }, [upgradeKey, upgradePointPromise, limit, value, displayThreshold, isValueBelowDisplayThreshold]); // Fetch purchase motions for usage limit banners only

  useEffect(() => {
    if (bannerData && bannerData.pointType === pointTypes.usageLimitBanner) {
      getLocationAwareProductPurchaseMotionData({
        locations: [USAGE_LIMIT_BANNER]
      }).then(response => {
        if (!response) {
          throw new Error('Response is empty');
        }

        setPurchaseMotionResponse(response);
      }).catch(error => {
        if (error && error.status !== 0) {
          Raven.captureException(new Error('Unexpected error processing purchase-motions response'), {
            extra: {
              error
            }
          });
        }
      });
    }
  }, [bannerData]);

  if (bannerData && isFreePortal !== null && (bannerData.pointType === pointTypes.removeBrandingBanner || bannerData.pointType === pointTypes.lockedFeatureBanner)) {
    return /*#__PURE__*/_jsx(RemoveBrandingBanner, {
      upgradePoint: bannerData,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      canViewNewSeats: canViewNewSeats,
      isFreePortal: isFreePortal
    });
  }

  if (isValueBelowDisplayThreshold() || hasError) {
    return null;
  }

  if (bannerData && isFreePortal !== null && purchaseMotionResponse !== undefined && limit && value !== undefined && value >= 0 && (bannerData.pointType === pointTypes.usageLimitBanner || bannerData.pointType === pointTypes.usageLimit)) {
    return /*#__PURE__*/_jsx(UsageLimitBanner, {
      upgradePoint: bannerData,
      limit: limit,
      value: value,
      hasReachedLimit: value >= limit,
      displayThreshold: displayThreshold,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      purchaseMotionData: purchaseMotionResponse,
      canViewNewSeats: canViewNewSeats,
      isFreePortal: isFreePortal
    });
  }

  if (bannerData && limit && value !== undefined && value >= 0 && bannerData.pointType === pointTypes.limitPackBanner) {
    return /*#__PURE__*/_jsx(LimitPackBanner, {
      upgradePoint: bannerData,
      limit: limit,
      value: value,
      displayThreshold: displayThreshold,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      canViewNewSeats: canViewNewSeats
    });
  }

  return /*#__PURE__*/_jsx(LoadingBanner, {
    className: className,
    "data-test-id": dataTestId,
    style: style
  });
};

export default UIUpgradeBanner;